import { Client } from "@seneca/client-library";

import { StudentReportResponse, TeacherReportResponse } from "./types";

export type CreateDataRequestBody = {
  classIds?: string[];
  schoolId?: string;
  wholeSchoolReport?: boolean;
  startDate: string;
  endDate: string;
};

export type RetryDataRequestBody = {
  requestId: string;
};

export default class SchoolAnalyticsServiceClient extends Client {
  async createDataRequest(body: CreateDataRequestBody) {
    const url = `${this.url}/api/schoolAnalytics/me`;
    const expectedResponses = [201];

    const response = await this.requestMaker.makeRequest(
      url,
      {
        method: "POST",
        body: JSON.stringify(body)
      },
      expectedResponses
    );

    return response.json;
  }

  async fetchMyDataRequests() {
    return this._pageMethod({
      method: this.fetchPageOfDataRequests.bind(this)
    });
  }

  async fetchPageOfDataRequests(options: Record<string, any> = {}) {
    const expectedResponses = [200];

    const baseUrl = `${this.url}/api/schoolAnalytics/me`;

    const params = this._generatePagingQueryParams(options);

    const url = params.length > 0 ? `${baseUrl}?${params.join("&")}` : baseUrl;

    const response = await this.requestMaker.makeRequest<any>(
      url,
      {
        method: "GET"
      },
      expectedResponses
    );

    return response.json;
  }

  async fetchDataRequest(requestId: string) {
    const url = `${this.url}/api/schoolAnalytics/me/${requestId}`;
    const expectedResponses = [200];

    const response = await this.requestMaker.makeRequest<any>(
      url,
      {
        method: "GET"
      },
      expectedResponses
    );

    return response.json;
  }

  async retryFailedRequest(body: RetryDataRequestBody) {
    const url = `${this.url}/api/schoolAnalytics/retry/me`;
    const expectedResponses = [200];

    const response = await this.requestMaker.makeRequest(
      url,
      {
        method: "POST",
        body: JSON.stringify(body)
      },
      expectedResponses
    );

    return response.json;
  }

  async fetchSchoolStudentsReport({
    startDate,
    endDate,
    yearGroups,
    departments
  }: {
    startDate: string;
    endDate: string;
    yearGroups?: number[];
    departments?: string[];
  }) {
    const url = `${this.url}/api/reports/students`;
    const expectedResponses = [200];

    const filters: { yearGroups?: number[]; departments?: string[] } = {};

    if (yearGroups && yearGroups.length > 0) {
      filters.yearGroups = yearGroups;
    }
    if (departments && departments.length > 0) {
      filters.departments = departments;
    }

    const response = await this.requestMaker.makeRequest<StudentReportResponse>(
      url,
      {
        method: "POST",
        body: JSON.stringify({
          startDate,
          endDate,
          ...(Object.keys(filters).length && { filters })
        })
      },
      expectedResponses
    );

    return response.json;
  }

  async fetchSchoolTeachersReport({
    startDate,
    endDate,
    yearGroups,
    departments
  }: {
    startDate: string;
    endDate: string;
    yearGroups?: number[];
    departments?: string[];
  }) {
    const url = `${this.url}/api/reports/teachers`;
    const expectedResponses = [200];

    const filters: { yearGroups?: number[]; departments?: string[] } = {};

    if (yearGroups && yearGroups.length > 0) {
      filters.yearGroups = yearGroups;
    }
    if (departments && departments.length > 0) {
      filters.departments = departments;
    }

    const response = await this.requestMaker.makeRequest<TeacherReportResponse>(
      url,
      {
        method: "POST",
        body: JSON.stringify({
          startDate,
          endDate,
          ...(Object.keys(filters).length && { filters })
        })
      },
      expectedResponses
    );

    return response.json;
  }
}
